// API Response Handler Utility
// Normalizes different API response structures to a consistent format

/**
 * Normalize API response to a consistent structure
 * @param {Object} response - Axios response object
 * @param {String} dataKey - Optional key for data in response (e.g., 'users', 'datarooms')
 * @returns {Object} Normalized response with data and pagination
 */
export const normalizeApiResponse = (response, dataKey = null) => {
  // If response is not an object, return it as is
  if (!response || typeof response !== 'object') {
    return {
      data: [],
      pagination: null,
      error: 'Invalid response format'
    };
  }

  // Get the actual response data (handle axios response structure)
  const responseData = response.data || response;

  // Initialize the normalized structure
  let normalized = {
    data: [],
    pagination: null,
    meta: {}
  };

  // Case 1: Direct array response
  if (Array.isArray(responseData)) {
    normalized.data = responseData;
    return normalized;
  }

  // Case 2: Laravel paginated response with 'data' key
  if (responseData.data !== undefined) {
    // Standard Laravel pagination structure
    normalized.data = Array.isArray(responseData.data) ? responseData.data : [responseData.data];
    
    // Extract pagination metadata
    if (responseData.current_page !== undefined) {
      normalized.pagination = {
        current_page: responseData.current_page || 1,
        per_page: responseData.per_page || 15,
        total: responseData.total || 0,
        last_page: responseData.last_page || 1,
        from: responseData.from || 0,
        to: responseData.to || 0
      };
    }
    
    // Extract any additional metadata
    if (responseData.meta) {
      normalized.meta = responseData.meta;
    }
    
    return normalized;
  }

  // Case 3: Response with custom data key (e.g., 'users', 'datarooms', 'invitations')
  if (dataKey && responseData[dataKey] !== undefined) {
    normalized.data = Array.isArray(responseData[dataKey]) 
      ? responseData[dataKey] 
      : [responseData[dataKey]];
    
    // Also set the custom key in the normalized response for backward compatibility
    normalized[dataKey] = normalized.data;
    
    // Check for pagination object
    if (responseData.pagination) {
      normalized.pagination = responseData.pagination;
    }
    // Check for inline pagination metadata
    else if (responseData.current_page !== undefined) {
      normalized.pagination = {
        current_page: responseData.current_page || 1,
        per_page: responseData.per_page || 15,
        total: responseData.total || 0,
        last_page: responseData.last_page || 1,
        from: responseData.from || 0,
        to: responseData.to || 0
      };
    }
    
    return normalized;
  }

  // Case 4: Check for common data keys if no specific key provided
  const commonDataKeys = ['data', 'items', 'results', 'records', 'users', 'datarooms', 
                          'invitations', 'templates', 'plans', 'features', 'roles', 'permissions', 'tags'];
  
  for (const key of commonDataKeys) {
    if (responseData[key] !== undefined && (Array.isArray(responseData[key]) || typeof responseData[key] === 'object')) {
      normalized.data = Array.isArray(responseData[key]) 
        ? responseData[key] 
        : [responseData[key]];
      
      // Extract pagination if exists
      if (responseData.pagination) {
        normalized.pagination = responseData.pagination;
      } else if (responseData.current_page !== undefined) {
        normalized.pagination = {
          current_page: responseData.current_page || 1,
          per_page: responseData.per_page || 15,
          total: responseData.total || 0,
          last_page: responseData.last_page || 1,
          from: responseData.from || 0,
          to: responseData.to || 0
        };
      }
      
      break;
    }
  }

  // Case 5: Success/Status response
  if (responseData.success !== undefined || responseData.status !== undefined) {
    normalized.success = responseData.success || (responseData.status === 'success');
    normalized.message = responseData.message || '';
    normalized.data = responseData.data || responseData;
    return normalized;
  }

  // Case 6: Single object response (for detail endpoints)
  if (normalized.data.length === 0 && Object.keys(responseData).length > 0) {
    // Check if it looks like a single record response
    if (responseData.id || responseData.uuid || responseData.email || responseData.name) {
      normalized.data = responseData;
      normalized.isSingle = true;
    } else {
      // Fallback: treat entire response as data
      normalized.data = responseData;
    }
  }

  return normalized;
};

/**
 * Extract error message from API error response
 * @param {Object} error - Axios error object
 * @returns {String} Error message
 */
export const extractErrorMessage = (error) => {
  if (!error) return 'An unknown error occurred';
  
  // Check for response error
  if (error.response) {
    const { data } = error.response;
    
    // Check for validation errors
    if (data.errors) {
      // Laravel validation errors
      const firstError = Object.values(data.errors)[0];
      return Array.isArray(firstError) ? firstError[0] : firstError;
    }
    
    // Check for message
    if (data.message) {
      return data.message;
    }
    
    // Check for error field
    if (data.error) {
      return data.error;
    }
    
    // Status text fallback
    return error.response.statusText || 'Request failed';
  }
  
  // Network error
  if (error.request) {
    return 'Network error - please check your connection';
  }
  
  // Other errors
  return error.message || 'An unknown error occurred';
};

/**
 * Handle paginated API response and update state
 * @param {Object} response - API response
 * @param {Function} setData - State setter for data
 * @param {Function} setPagination - State setter for pagination
 * @param {String} dataKey - Optional data key
 */
export const handlePaginatedResponse = (response, setData, setPagination, dataKey = null) => {
  const normalized = normalizeApiResponse(response, dataKey);
  
  // Set data
  if (normalized.isSingle) {
    setData(normalized.data);
  } else {
    setData(Array.isArray(normalized.data) ? normalized.data : []);
  }
  
  // Set pagination if exists
  if (setPagination && normalized.pagination) {
    setPagination(normalized.pagination);
  }
  
  return normalized;
};

export default {
  normalizeApiResponse,
  extractErrorMessage,
  handlePaginatedResponse
};